import { type ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';

type StatusVariant = 'coming-soon' | 'not-connected' | 'empty';

interface ActivityEmptyStateProps {
  icon: LucideIcon;
  title: string;
  description: string;
  status?: StatusVariant;
  action?: ReactNode;
}

const statusPills: Record<StatusVariant, { label: string; classes: string }> = {
  'coming-soon': {
    label: 'Coming soon',
    classes: 'bg-coral-50 text-coral-600 border-coral-200 animate-pulse',
  },
  'not-connected': {
    label: 'Not connected',
    classes: 'bg-surface-subtle text-muted border-line',
  },
  empty: {
    label: '',
    classes: '',
  },
};

export function ActivityEmptyState({
  icon: Icon,
  title,
  description,
  status,
  action,
}: ActivityEmptyStateProps) {
  const pill = status ? statusPills[status] : null;

  return (
    <div className="bg-white rounded-lg shadow-warm-sm border border-line">
      {/* Header */}
      <div className="px-4 py-3 border-b border-line flex items-center justify-between">
        <span className="text-sm font-semibold text-text uppercase tracking-wider">
          {title}
        </span>
        {pill && pill.label && (
          <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full border ${pill.classes}`}>
            {pill.label}
          </span>
        )}
      </div>

      {/* Body */}
      <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
        <Icon className="w-8 h-8 text-subtle mb-3" />
        <p className="text-xs text-muted max-w-xs">{description}</p>
        {action && <div className="mt-4">{action}</div>}
      </div>
    </div>
  );
}
